import { Dialog } from "@headlessui/react";
import { useDealerDiscounts } from "app/hooks/checkout/useDealerDiscounts";
import { useDealerPriceInformations } from "app/hooks/checkout/useDealerPriceInformations";
import { useConfigurationOptionsContext } from "app/providers/DoorOptionsProvider";
import { BusinessDoorModal } from "entities/shared/product-modal/components/BusinessDoorModal";
import {
  zakelijkKozijnCodes,
  zakelijkProfielCodes,
} from "features/option-controls/door-width-controls/ZakelijkHelper";
import Skeleton from "react-loading-skeleton";
import { Product } from "shared/api/middleware-api-client";
import Modal, { ModalType } from "shared/ui/modal";
import Heading from "shared/ui/typography/Heading";
import { getFormattedPrice } from "shared/utils/getFormattedPrice";
import MonthlyPayment from "widgets/shared/monthly-payment";
import { BusinessProfielModal } from "./BusinessProfielModal";
import { BusinessKozijnModal } from "./BusinessKozijnModal";

export interface ProductModalProps {
  isOpen: boolean;
  totalPrice: string;
  monthlyPayment: number | null;
  setIsOpen: (value: boolean) => void;

  product: Product;
}

const BusinessProductModal = (props: ProductModalProps) => {
  const { extra, selectedDoor } = useConfigurationOptionsContext();

  //Total price of product
  const totalPrice = selectedDoor?.price ?? 0;

  // Dealer discounts, loading
  const { isDealerDiscountsLoading } = useDealerDiscounts(totalPrice);

  const { totalZakelijkPrice, totalZakelijkPriceDiscounted, vatAmount } =
    useDealerPriceInformations();

  if (!selectedDoor) return null;

  const doorPrice = "€" + (props.product.doorType.price ?? 0).toFixed(2);

  //Is profiel product
  const isProfielProduct = zakelijkProfielCodes.includes(selectedDoor.code);

  // Is kozijn product
  const isKozijnProduct = zakelijkKozijnCodes.includes(selectedDoor.code);
  return (
    <Modal
      isOpen={props.isOpen}
      setIsOpen={props.setIsOpen}
      type={ModalType.SQUARE}
      hideHeader={true}
      hasProductFooter={true}
      className="gg-overflow-visible"
      businessModal={true}
    >
      <Dialog.Panel>
        <div className="gg-relative gg-m-auto gg-w-1/2 gg-pt-4"></div>

        <Heading
          data-testid="modalHeading"
          headingLevel="h2"
          className="gg-leading-[1.5rem]"
        >
          Jouw configuratie is aan je winkelmand toegevoegd!
        </Heading>
        <section className="gg-mt-2 gg-py-5">
          <Heading headingLevel="h3" className="gg-pb-3.5">
            {props.product.doorType.name}
          </Heading>

          {isProfielProduct ? (
            <BusinessProfielModal product={props.product} extra={extra} />
          ) : isKozijnProduct ? (
            <BusinessKozijnModal product={props.product} extra={extra} />
          ) : (
            <BusinessDoorModal product={props.product} extra={extra} />
          )}
        </section>

        {/* Discount section */}
        {!isProfielProduct && (
          <section className="gg-flex gg-justify-between gg-pb-5">
            <div className="gg-flex gg-items-center">
              <Heading headingLevel="h5">
                {" "}
                {props.product.doorType.name}
              </Heading>
            </div>
            <div className="gg-flex gg-items-center gg-text-end">
              <div className="gg-flex gg-flex-col">
                {/* Price formatted output */}
                <span className="gg-ml-0.5 gg-text-sm gg-text-purple-300">
                  {doorPrice}
                </span>
              </div>
            </div>
          </section>
        )}
        {/* Totaal section */}
        <section className="gg-flex gg-items-center gg-justify-between gg-pb-5">
          <div>
            <Heading headingLevel="h4">Totaal</Heading>
            <p className="gg-inline-flex gg-items-center gg-text-sm gg-font-light gg-text-gray-400">
              Incl. BTW
            </p>
          </div>
          <div className="gg-flex gg-items-center gg-text-end">
            <div className="gg-flex gg-flex-col">
              <h4 className="gg-self-end gg-text-sm gg-font-bold gg-text-gray-400 gg-line-through">
                {!isDealerDiscountsLoading ? (
                  totalZakelijkPrice
                ) : (
                  <Skeleton width={50} />
                )}
              </h4>

              <h4 className="gg-text-lg gg-font-bold gg-text-red">
                {!isDealerDiscountsLoading ? (
                  totalZakelijkPriceDiscounted
                ) : (
                  <Skeleton width={90} />
                )}
              </h4>
              <p className="gg-mt-0.5 gg-text-xs gg-font-light gg-text-gray-500">
                incl. €{vatAmount} btw
              </p>
            </div>
          </div>
        </section>
      </Dialog.Panel>
    </Modal>
  );
};

export default BusinessProductModal;
